import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import SubHeader from "components/common/SubHeader";
import {
    postReservation,
    cancelReservation,
    getClassScheduleDetail,
    getPracticeScheduleDetail,
    applyPracticeSchedule,
} from "api/class";
import { useSnackbar } from "contexts/SnackbarContext";
import Divider from "components/Divider";
import LocationMap from "pages/location/LocationMap";
import useUserStore from "@store/useUserStore";
import Blank from "components/common/Blank";
import locationIcon from "assets/images/icons/ic_location.svg";
import userIcon from "assets/images/icons/ic_user_line.svg";
import waitingIcon from "assets/images/icons/ic_waiting.svg";
import LoadingSpinner from "components/common/LoadingSpiner";
import BuyTicketBottomsheet from "components/reservation/BuyTicketBottomsheet";
import LoginBottomSheet from "components/common/LoginBottomSheet";
import CommonModal from "components/common/CommonModal";
import PoolDetailTable from "components/reservation/PoolDetailTable";
import useRefreshCallback from "hooks/useRefreshCallback";
import useShare from "hooks/useShare";
import shareIcon from "assets/images/icons/ic_share.svg";
import ErrorPage from "pages/error/ErrorPage";
import "pages/schedule/SportsLearning.scss";
import Within3DaysNotification from "components/reservation/Within3DaysNotification";
import Day3CertificationNotification from "components/reservation/Day3CertificationNotification";
import { renderWithLinks } from "utils/renderWithLinks";
import PersonChip from "components/profile/PersonChip";
import ProfileAvatar from "components/common/ProfileAvatar";
import { maskName } from "utils/util";
import { getPublicUserKey, isSameUser } from "utils/userIdentity";
import { IDENTITY_STATUS, usePublicIdentityStatus } from "hooks/usePublicIdBackfill";
import { startDirectMessageRoom } from "api/chat";

export const DEFAUTL_COMMENT = `🔔연습반 필독🔔\n ▶ 교육생간 티칭 금지 (간단 피드백 OK)\n ▶ 연습반 버디시스템 준수\n ▶ 무리한 트레이닝 금지\n ▶ 노핀다이빙 금지 (노핀 교육 후 동의를 얻은 분만 가능)\n 🚫 위 사항들을 어길시 잠수풀 입장에 제한이 생길 수 있습니다. (강사 포함)`

const PRACTICE_APPLY_ERROR_MESSAGES = {
    RESERVATION_NOT_RESERVED: "예약이 더 이상 유효하지 않습니다",
    RESERVATION_IS_WAITING: "대기자는 결제할 수 없습니다",
    RESERVATION_NOT_PENDING: "이미 처리된 예약입니다",
    RESERVATION_ALREADY_EXISTS: "이미 해당 연습반에 예약되어 있습니다",
    TICKET_ALREADY_IN_USE: "해당 횟수권은 이미 다른 예약에 사용 중입니다",
    NO_TICKET_AVAILABLE: "횟수권 사용에 실패했습니다",
    PAYMENT_DEADLINE_EXPIRED: "결제 기한이 만료되어 예약이 취소되었습니다",
    PAYMENT_PROCESSING_ERROR: "결제 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요",
    INVALID_PRICE_DATA: "가격 정보 오류 — 관리자에게 문의해주세요",
};

const ClassReservationDetail = () => {
    const userInfo = useUserStore((state) => state.userInfo);
    const navigate = useNavigate();
    const location = useLocation();
    const type = location.pathname.includes("class") ? "class" : "practice";
    const params = useParams();
    const scheduleId = type === "class" ? params.classId : params.practiceId;
    const today = dayjs();

    const { showError, showSuccess } = useSnackbar();
    const identityStatus = usePublicIdentityStatus();
    const { share } = useShare();

    const [isLoading, setIsLoading] = useState(false);
    const [title, setTitle] = useState(""); // 헤더 타이틀
    const [myStatus, setMyStatus] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [errorStatus, setErrorStatus] = useState(null);
    const [poolInfoModal, setPoolInfoModal] = useState(false);
    const [showBuyTicketBottomSheet, setShowBuyTicketBottomSheet] =
        useState(false);
    const [reservationErrorData, setReservationErrorData] = useState(null); // 예약 에러 데이터
    const [showLoginBottomSheet, setShowLoginBottomSheet] = useState(false);
    const [showCancelReservationModal, setShowCancelReservationModal] =
        useState(false);
    const [showCancelWaitingModal, setShowCancelWaitingModal] =
        useState(false);
    const [showWithin3DaysNotification, setShowWithin3DaysNotification] =
        useState(false);
    const [showDay3Notification, setShowDay3Notification] = useState(false);
    const [certifications, setCertifications] = useState([]);
    const [instructorComment, setInstructorComment] = useState("");

    useRefreshCallback(() => {
        if (type === "class") {
            return getClassScheduleDetail(scheduleId).then((res) => {
                setSchedule({
                    location_name: res.location_name || "",
                    lesson_product_id: res.lesson_product_id,
                    title: res.lesson_product_display_name || res.title || "",
                    location: res.location_name || "",
                    instructor: res.instructor_name || "",
                    instructor_detail: res.instructor || null,
                    date: res.date || "",
                    start_time: res.start_time || "",
                    end_time: res.end_time || "",
                    capacity: res.capacity || 0,
                    reservations: {
                        confirmed: res.reservations.confirmed || [],
                        waiting: res.reservations.waiting || [],
                    },
                    lesson_product_name: res.lesson_product_name || "",
                    sport_name: res.sport_name || "",
                });
                setCertifications(res.associations_detail || []);
                setInstructorComment(
                    res?.instructor_comment || res?.note|| "입력된 내용이 없습니다."
                );
            }).catch((error) => setErrorStatus(error.response?.status === 404 ? 404 : 500));
        } else {
            return getPracticeScheduleDetail(scheduleId).then((res) => {
                setSchedule({
                    location_name: res.location_name || "",
                    title: res.title || "",
                    location: res.location_name || "",
                    instructor: res.instructor_name || "",
                    instructor_detail: res.instructor || null,
                    date: res.date || "",
                    start_time: res.start_time || "",
                    end_time: res.end_time || "",
                    capacity: res.capacity || 0,
                    reservations: res.reservations,
                    sport_name: res.sport_name || "",
                    price: res.discount_price ?? res.current_price ?? Number(res.price)
                });
                setInstructorComment(
                    res?.note || "입력된 내용이 없습니다."
                );
            }).catch((error) => setErrorStatus(error.response?.status === 404 ? 404 : 500));
        }
    }, [scheduleId, type]);

    useEffect(() => {
        if (type === "class") {
            setTitle("수업 예약");
            getClassScheduleDetail(scheduleId).then((res) => {
                setSchedule({
                    location_name: res.location_name || "",
                    lesson_product_id: res.lesson_product_id,
                    title: res.lesson_product_display_name || res.title || "",
                    location: res.location_name || "",
                    instructor: res.instructor_name || "",
                    instructor_detail: res.instructor || null,
                    date: res.date || "",
                    start_time: res.start_time || "",
                    end_time: res.end_time || "",
                    capacity: res.capacity || 0,
                    reservations: {
                        confirmed: res.reservations.confirmed || [],
                        waiting: res.reservations.waiting || [],
                    },
                    lesson_product_name: res.lesson_product_name || "",
                    sport_name: res.sport_name || "",
                });
                setCertifications(res.associations_detail || []);

                setInstructorComment(
                    res?.instructor_comment || "입력된 내용이 없습니다."
                );
            }).catch((error) => {
                setErrorStatus(error.response?.status === 404 ? 404 : 500);
            });
        } else {
            setTitle("연습반 예약");
            getPracticeScheduleDetail(scheduleId).then((res) => {
                setSchedule({
                    location_name: res.location_name || "",
                    title: res.title || "",
                    location: res.location_name || "",
                    instructor: res.instructor_name || "",
                    instructor_detail: res.instructor || null,
                    date: res.date || "",
                    start_time: res.start_time || "",
                    end_time: res.end_time || "",
                    capacity: res.capacity || 0,
                    reservations: res.reservations,
                    sport_name: res.sport_name || "",
                    price: res.discount_price ?? res.current_price ?? Number(res.price)
                });
                // 연습반 코멘트 없는 경우 기본 코멘트 사용
                setInstructorComment(
                    res?.note || DEFAUTL_COMMENT
                );
            }).catch((error) => {
                setErrorStatus(error.response?.status === 404 ? 404 : 500);
            });
        }
    }, [scheduleId, type]);

    const handleStartMessage = async (userId) => {
      if (!userInfo) {
          navigate("/login");
          return;
      }
      try {
          const room = await startDirectMessageRoom(userId);
          if (room?.id) {
              navigate(`/messages/${encodeURIComponent(room.id)}`);
              return;
          }
          showError("메시지방을 시작하지 못했습니다.");
      } catch {
          showError("메시지방을 시작하지 못했습니다.");
      }
  };

    // DAY3 수업은 신청 전 자격증 협회·이론/이러닝 완료 안내를 먼저 노출한다
    const isDay3Class = type === "class" && !!schedule?.title?.includes("Day 3");

    const proceedReservation = () => {
        if (dayjs(schedule.date).diff(today, "day") <= 3) {
            setShowWithin3DaysNotification(true);
            return;
        }
        handleReservation(scheduleId);
    };

    // 예약 후 10분 이내인지 확인하는 함수
    const isWithin10MinutesOfReservation = () => {
        const reservationTime = sessionStorage.getItem("reservationTime");
        if (reservationTime === null) return false;

        const reservedAt = dayjs(reservationTime);
        const now = dayjs();
        const diffInMinutes = now.diff(reservedAt, "minute");

        return diffInMinutes < 10;
    };

    useEffect(() => {
        if (schedule === null) return;
        const isPast = dayjs(schedule.date).isBefore(today, "day");
        const isLeftMoreThan2Days =
            dayjs(schedule.date).diff(today, "day") >= 2;

        // 예약 payload의 사용자 신원은 reservation.user 에 있다.
        const comFirmedUsers = schedule.reservations.confirmed;
        const alreadyReserved = !!comFirmedUsers.find(
            (reservation) => isSameUser(reservation.user, userInfo)
        );
        if (isPast) {
            setMyStatus("not_cancellable"); //날짜 지남 - 취소불가
            return;
        }

        const waitingUsers = schedule.reservations.waiting || [];
        const alreadyWaiting = !!waitingUsers.find(
            (reservation) => isSameUser(reservation.user, userInfo)
        );
 
        if (alreadyReserved && isLeftMoreThan2Days === false) {
            // 수업 3일 이내이지만, 예약 후 10분 이내라면 취소 가능
            if (isWithin10MinutesOfReservation()) {
                setMyStatus("cancellable_within_10min"); // 예약 후 10분 이내 - 취소 가능
            } else {
                setMyStatus("not_cancellable"); // 예약했고 + 수업 3일 이내 남음 + 10분 초과 - 취소 불가
            }
            return;
        } else if (alreadyReserved) {
            setMyStatus("cancellable"); // 예약했고 + 수업 3일 이상 남음 - 예약 취소
            return;
        } else if (alreadyWaiting) {
            setMyStatus("waiting_cancel"); // 대기 중 - 대기 취소
            return;
        } else {
            if (schedule.capacity === schedule.reservations.confirmed.length) {
                setMyStatus("waiting"); // 대기하기
                return;
            } else {
                setMyStatus("reserve"); // 예약하기
            }
            return;
        }
        // userInfo 의존 필수: public_id 는 backfill 로 늦게 채워질 수 있고,
        // 그때 예약/대기 판정을 다시 계산하지 않으면 잘못된 액션이 노출된 채 남는다.
    }, [schedule, userInfo]);

    const handleReservation = async (id) => {
        if (isLoading) return;
        setIsLoading(true);

        try {
            // 1. 로그인 확인
            if (userInfo === null) {
                setShowLoginBottomSheet(true);
                setIsLoading(false);
                return;
            }

            // 2. 예약 마감 검증 (전날 17시 이후 불가)
            const deadline = dayjs(schedule.date).subtract(1, "day").hour(17).minute(0).second(0);
            if (dayjs().isAfter(deadline)) {
                showError("예약 가능 시간이 지났습니다. (전날 17:00까지)");
                setIsLoading(false);
                return;
            }

            // 3. 예약 API 호출 (백엔드에서 수강권, 중복 등 검증 처리)
            const res = await postReservation(type, id);

            // 4. 성공 응답 처리
            if (res?.status === 201) {
                if (res.data.queue_position === undefined) {
                    sessionStorage.setItem(
                        "reservationTime",
                        dayjs().format("YYYY-MM-DD HH:mm:ss")
                    );
                    showSuccess("예약이 완료되었습니다.", {
                        duration: 3000,
                        bottomPosition: 20,
                    });
                } else {
                    showSuccess(
                        `예약 대기 순서 ${res.data.queue_position}번째로 예약되었습니다.`,
                        {
                            duration: 3000,
                            bottomPosition: 20,
                        }
                    );
                }
                navigate(`/mybuccl?tab=예약`);
                return;
            }

            // 유료 연습/트레이닝일 때 결제창 띄우기
            if(res?.data?.price > 0){
              const paymentInfo = res.data.payment_params
              window.innopay.goPay({
                ...paymentInfo,
                buyerEmail: userInfo.user_email,
            });
              return
            }

            // 5. 에러 응답 처리
            handleReservationError(res);
        } catch (error) {
            showError("예약 중 오류가 발생했습니다. 다시 시도해주세요.");
        } finally {
            setIsLoading(false);
        }
    };

    // 연습반 유료 전환 — PENDING 예약 결제 완료 (BE PR #365)
    const handleApplyComplete = async () => {
        if (isLoading) return;
        if (!userInfo) {
            setShowLoginBottomSheet(true);
            return;
        }
        setIsLoading(true);
        // Innopay 모달이 떠있는 동안 setIsLoading(false)가 실행되어 버튼이 재활성화되면
        // 중복 주문이 생긴다. Case B 진입 시 paymentInFlight를 켜서 finally의 unlock을 차단.
        // returnUrl 페이지 전환으로 자연 리셋되며, 모달을 dismiss하고 재시도하려면
        // 사용자가 페이지를 새로고침해야 한다 (중복 결제 방지가 우선).
        let paymentInFlight = false;
        try {
            const res = await applyPracticeSchedule(scheduleId);
            const data = res?.data;

            // Case A: 다회권 즉시 차감
            if (res?.status === 200 && data?.payment_status === "PAID" && data?.ticket_id) {
                showSuccess("다회권으로 신청이 완료되었습니다.");
                navigate("/mybuccl?tab=예약");
                return;
            }

            // Case B: 카드 결제 진입
            if (res?.status === 200 && data?.payment_required && data?.payment_params) {
                if (!window.innopay) {
                    showError("결제 모듈이 아직 준비되지 않았습니다. 잠시 후 다시 시도해주세요.");
                    return;
                }
                paymentInFlight = true;
                window.innopay.goPay({
                    ...data.payment_params,
                    buyerEmail: userInfo.user_email,
                });
                return;
            }

            // Case C: 이미 결제됨
            if (res?.status === 200 && data?.payment_status === "PAID" && !data?.ticket_id) {
                showSuccess("이미 결제하신 신청입니다.");
                navigate("/mybuccl?tab=예약");
                return;
            }

            // 에러 9종
            const errorBody = res?.response?.data || data || {};
            const code = errorBody.code;
            const msg = PRACTICE_APPLY_ERROR_MESSAGES[code] || errorBody.error || "신청 처리 중 오류가 발생했습니다";
            showError(msg);
        } catch (e) {
            showError("신청 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
        } finally {
            if (!paymentInFlight) setIsLoading(false);
        }
    };

    // 예약 에러 처리 함수
    const handleReservationError = (errorResponse) => {
        const error = errorResponse?.response?.data || errorResponse;
        const errorCode = error?.code;
        const errorMessage = error?.error || "예약에 실패했습니다.";

        // 티켓 구매가 필요한 경우 (recommended_products 또는 recommended_product가 있는 경우)
        const ticketRequiredCodes = [
            "RETAKE_TICKET_REQUIRED", // 재수강 필요
            "LESSON_TICKET_REQUIRED", // 강습 티켓 필요
            "SPECIAL_TICKET_REQUIRED", // 특강권 필요
        ];

        // recommended_products (복수) 또는 recommended_product (단수) 확인
        const hasRecommendedProducts =
            error.recommended_products || error.recommended_product;

        if (ticketRequiredCodes.includes(errorCode) && hasRecommendedProducts) {
            // 에러 데이터 저장 후 구매 모달 표시
            setReservationErrorData(error);
            setShowBuyTicketBottomSheet(true);
            return;
        }

        // "사용 가능한 티켓이 없습니다" - lesson_product_id로 이동
        if (
            errorMessage.includes("사용 가능한 티켓이 없습니다") ||
            errorMessage.includes("티켓이 필요합니다")
        ) {
            if (schedule.lesson_product_id) {
                // lesson_product_id만 있는 경우
        
                setReservationErrorData({
                    error: errorMessage,
                    lessonProductId: schedule.lesson_product_id,
                });
                setShowBuyTicketBottomSheet(true);
                return;
            } else {
                console.log("⚠️ lesson_product_id 없음!");
            }
        }

        // 기타 에러 메시지 표시 (중복 예약, 날짜 순서, 시간 충돌 등)
        console.log("⚠️ Case 3: 기타 에러 → 토스트 메시지만 표시");
        showError(errorMessage);
    };


    const ReservationButton = () => {
        // 과거 날짜(지난 일정)는 어떤 액션도 불가하므로 버튼을 숨긴다.
        const isPast = dayjs(schedule?.date).isBefore(today, "day");
        if (isPast) return null;

        // 로그인 세션인데 public_id 가 아직 확정되지 않았으면 예약 여부를 판정할 수 없다.
        // 이 구간에 액션을 노출하면 이미 예약한 사용자에게 예약/결제 버튼이 뜬다.
        if (identityStatus === IDENTITY_STATUS.PENDING) return null;

        // 유료 연습반 예약자는 BE 가 내려주는 내 예약의 payment_status 로 분기한다.
        // PENDING(미결제)이면 결제를 마저 진행하는 [결제 완료하기]를 노출하고,
        // PAID 면 아래 switch 의 일반 예약 취소 플로우를 그대로 탄다.
        const isPaidPracticeReserved =
            type === "practice" &&
            schedule?.price > 0 &&
            (myStatus === "cancellable" ||
                myStatus === "not_cancellable" ||
                myStatus === "cancellable_within_10min");
        const myPaymentStatus =
            schedule?.reservations?.confirmed?.find((reservation) =>
                isSameUser(reservation.user, userInfo)
            )?.payment_status ?? null;

        if (isPaidPracticeReserved && myPaymentStatus !== "PAID") {
            return (
                <button
                    className="btn btn-giant btn-purple500"
                    onClick={handleApplyComplete}
                    disabled={isLoading}
                >
                    결제 완료하기
                </button>
            );
        }

        switch (myStatus) {
            case "reserve":
                return (
                    <button
                        className={`btn btn-giant btn-purple500`}
                        onClick={() => {
                            if (isDay3Class) {
                                setShowDay3Notification(true);
                                return;
                            }
                            proceedReservation();
                        }}
                    >
                        {schedule.price
                            ? `${schedule.price.toLocaleString()}원 결제하기`
                            : "예약하기"}
                    </button>
                );
            case "complete":
                return (
                    <button className="btn btn-orange300 btn-giant">
                        예약 완료
                    </button>
                );
            case "waiting":
                return (
                    <button
                        className="btn btn-purple500 btn-giant"
                        onClick={() => handleReservation(scheduleId)}
                    >
                        예약 대기
                    </button>
                );
            case "waiting_cancel":
                return (
                    <button
                        className="btn btn-orange300 btn-giant"
                        onClick={() => setShowCancelWaitingModal(true)}
                    >
                        대기 취소
                    </button>
                );
            case "cancellable":
            case "cancellable_within_10min":
                return (
                    <button
                        className="btn btn-orange300 btn-giant"
                        onClick={() => {
                            setShowCancelReservationModal(true);
                        }}
                    >
                        예약 취소
                    </button>
                );
            case "not_cancellable":
                return (
                    <button className="btn btn-disabled btn-giant">
                        취소불가
                    </button>
                );
            case "finished":
                return (
                    <button className="btn btn-disabled btn-giant">
                        예약마감
                    </button>
                );
            default:
                return null;
        }
    };


    return (
        <div className="page-reservation-class-detail">
            <SubHeader
                title={title}
                menus={[{
                    name: '공유',
                    icon: shareIcon,
                    callback: () => share({
                        title,
                        appPath: type === "class" ? `/reservation/class/${scheduleId}` : `/reservation/practice/${scheduleId}`,
                    }),
                }]}
            />
            {!errorStatus && schedule === null && <LoadingSpinner />}
            {errorStatus && (
                <ErrorPage
                    status={errorStatus}
                    onRetry={errorStatus === 500 ? () => { setErrorStatus(null); setSchedule(null); } : undefined}
                />
            )}
            {schedule !== null && (
                <>
                    <div className="class-info-wrap">
                        <h2 className="title bold">
                            {type === "practice" ? schedule.title : `${schedule.sport_name} ${schedule.title}`}
                        </h2>
                        <p className="class-sub-info-text">
                            <span>
                                {dayjs(schedule.date).format(
                                    "YYYY. M. DD (ddd)"
                                )}
                                {` ${schedule.start_time.slice(
                                    0,
                                    5
                                )} ~ ${schedule.end_time.slice(0, 5)}`}
                            </span>
                        </p>
                        {(schedule.instructor_detail || schedule.instructor) && (() => {
                            const inst = schedule.instructor_detail || {};
                            const contact = [inst.hp, inst.email].filter(Boolean).join(" · ");
                            const uid = getPublicUserKey(inst);
                            return (
                                <div className="instructor-card">
                                    <div
                                        className="instructor-card__body"
                                        onClick={() => {
                                            if (uid) navigate(`/instructor/${encodeURIComponent(uid)}`);
                                        }}
                                        style={{ cursor: uid ? "pointer" : "default" }}
                                    >
                                        <ProfileAvatar
                                            imageUrl={inst.profile_image_url || null}
                                            size={56}
                                        />
                                        <div className="instructor-card__info">
                                            <div className="instructor-card__row instructor-card__row--top">
                                                <span className="instructor-card__name">
                                                    {inst.name || schedule.instructor}
                                                </span>
                                                {inst.brand && (
                                                    <span className="instructor-card__brand">
                                                        {inst.brand}
                                                    </span>
                                                )}
                                                <span className="instructor-card__role">강사</span>
                                            </div>
                                            {contact && (
                                                <div className="instructor-card__row instructor-card__row--bottom">
                                                    {contact}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="community-profile-wrap__dm-btn"
                                        onClick={() => {handleStartMessage(inst.public_id)}}
                                        aria-label="메세지 보내기"
                                    >
                                        메세지
                                    </button>
                                </div>
                            );
                        })()}
                        <ul className="reservation-class-info">
                            <li
                                className="bold"
                                style={{ textDecoration: "underline" }}
                                onClick={() => {
                                    setPoolInfoModal(true);
                                }}
                            >
                                <img src={locationIcon} alt="location" />
                                {schedule.location}
                            </li>
                            <li className="reservation-participants-li">
                                <img src={userIcon} alt="user" />
                                <span className="reservation-participants-li__label">
                                    {`예약자 (${schedule.current_bookings || schedule.reservations.confirmed.length} / ${schedule.capacity}명)`}
                                </span>
                                {schedule.reservations.confirmed.length !== 0 && (
                                    <div className="person-chip-list">
                                        {schedule.reservations.confirmed.map((r, idx) => (
                                            <PersonChip
                                                key={r.user_id || r.id || idx}
                                                name={maskName(r.user?.name || r.name, !!userInfo)}
                                                imageUrl={r.user?.profile_image_url}
                                                userId={getPublicUserKey(r.user) || getPublicUserKey(r)}
                                                size={44}
                                            />
                                        ))}
                                    </div>
                                )}
                            </li>
                            {(schedule.reservations.waiting || []).length > 0 && (
                                <li className="reservation-participants-li">
                                    <img src={waitingIcon} alt="waiting" />
                                    <span className="reservation-participants-li__label">대기자</span>
                                    <div className="person-chip-list">
                                        {schedule.reservations.waiting.map((r, idx) => (
                                            <PersonChip
                                                key={r.user_id || r.id || idx}
                                                name={maskName(r.user?.name || r.name, !!userInfo)}
                                                imageUrl={r.user?.profile_image_url}
                                                userId={getPublicUserKey(r.user) || getPublicUserKey(r)}
                                                size={44}
                                            />
                                        ))}
                                    </div>
                                </li>
                            )}
                            {schedule.title.includes("초급") &&
                                schedule.title.includes("Day 3") && (
                                    <li>{`* 발급 가능 자격증 : ${
                                        certifications.length > 0
                                            ? certifications
                                                  .map((c) => c.name)
                                                  .join(", ")
                                            : "별도 문의"
                                    }`}</li>
                                )}
                        </ul>
                        <div className="instructor-comment">
                            <p className="bold">강사님 안내사항</p>
                            <p>{instructorComment.length > 0 ? renderWithLinks(instructorComment) : '입력된 내용이 없습니다'}</p>
                        </div>
                    </div>
                    <Divider />
                    <div className="reservation-info-wrap">
                        <p className="info-title">🕗 예약/취소 가능 시간</p>
                        <div className="contents">
                            <p>
                                <span className="bold">{`예약   `} </span>
                                {`${dayjs(schedule.date)
                                    .subtract(1, "days")
                                    .format("YYYY. M. DD (ddd)")} 17:00 `}
                                까지 가능
                            </p>
                            <p>
                                <span className="bold">{`취소   `} </span>
                                {`${dayjs(schedule.date)
                                    .subtract(3, "days")
                                    .format("YYYY. M. DD (ddd)")} 23:59 `}
                                까지 가능
                            </p>
                            <p>
                                <strong>* 수업 변경은</strong> 수업 취소 후 다시
                                예약하시면 됩니다.
                            </p>
                        </div>
                    </div>
                    <div
                        className="reservation-info-wrap"
                        
                    >
                        <p className="info-title">📝 수업 안내</p>
                        <PoolDetailTable locationName={schedule.location} />
                    </div>
                    
                    
                    <LocationMap
                        info={{
                            name: schedule.location,
                        }}
                        hasOpenAppBtn={true}
                    />
                 
                    <div className="reservation-info-wrap">
                        <p
                            className="info-title"
                            style={{ color: "#757B80", marginTop: "35px" }}
                        >
                            ⚠️ 취소 수수료 안내
                        </p>
                        <ul className="contents">
                            <li>
                              앱 내 예약 취소 및 변경은 예약/취소 가능 시간까지만 가능합니다.<br/>
                              <strong>* 예약 취소는 수업 3일 전까지 가능합니다.</strong>
                            </li>
                            <li>
                              예약/취소 가능 시간 이후 취소 및 변경 요청은 카카오 채널을 통해 접수되며, 불가피한 사유가 없는 경우 아래 기준에 따라 <strong>취소 수수료 또는 강습 회차 차감</strong>이 발생할 수 있습니다.<br/>
                              * 연습반 : 취소 가능 시간 이후 취소 시 풀장 입장료 100% 발생<br/>
                              * 강습 : 취소 가능 시간 이후부터 수업 전일 23:59까지 취소 시 20,000원 발생<br/>
                              * 수업 당일 취소 / 노쇼 : 당일 수업이 진행된 것으로 간주되어 해당 회차의 강습 횟수 차감<br/>
                              * 수업 당일 취소 또는 노쇼로 차감된 강습을 다시 진행하려면 재수강권을 별도 구매해야 합니다.<br/>
                            </li>
                            <li>단, 불가피한 사유가 있는 경우 증빙 확인 후 내부 기준에 따라 예외 적용될 수 있습니다.</li>
                        </ul>
                    </div>
                    <div className="btn-wrap btn-bottom-fixed">
                        {identityStatus === IDENTITY_STATUS.UNAVAILABLE && (
                            <p className="reservation-identity-notice">
                                예약 상태를 확인할 수 없어요. 앱을 새로고침하면 정확한 상태가 표시됩니다.
                            </p>
                        )}
                        <ReservationButton />
                    </div>
                    <Blank height={100} />
                    <Day3CertificationNotification
                        isOpen={showDay3Notification}
                        onClose={() => setShowDay3Notification(false)}
                        onContinue={() => {
                            setShowDay3Notification(false);
                            proceedReservation();
                        }}
                        certifications={certifications}
                    />
                    <Within3DaysNotification
                        isOpen={showWithin3DaysNotification}
                        onClose={() => setShowWithin3DaysNotification(false)}
                        onContinue={() => {
                            setShowWithin3DaysNotification(false);
                            handleReservation(scheduleId);
                        }}
                    />
                    <BuyTicketBottomsheet
                        isOpen={showBuyTicketBottomSheet}
                        onClose={() => {
                            setShowBuyTicketBottomSheet(false);
                            setReservationErrorData(null);
                        }}
                        errorData={reservationErrorData}
                        lessonProductId={schedule?.lesson_product_id}
                    />
                    <LoginBottomSheet
                        isOpen={showLoginBottomSheet}
                        onClose={() => setShowLoginBottomSheet(false)}
                    />
                    <CommonModal
                        isOpen={showCancelReservationModal}
                        onClose={() => setShowCancelReservationModal(false)}
                    >
                        <div style={{ padding: "8px 0 4px" }}>
                            <p style={{ fontSize: "16px", fontWeight: "600", color: "#111", marginBottom: "24px", lineHeight: "1.5" }}>
                                예약을 취소하시겠습니까?
                            </p>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    className="btn btn-gray200"
                                    style={{ flex: 1, height: "46px", borderRadius: "8px", fontSize: "15px" }}
                                    onClick={() => setShowCancelReservationModal(false)}
                                >
                                    닫기
                                </button>
                                <button
                                    className="btn btn-purple500"
                                    style={{ flex: 1, height: "46px", borderRadius: "8px", fontSize: "15px" }}
                                    onClick={() => {
                                        cancelReservation(type, scheduleId).then((res) => {
                                            setShowCancelReservationModal(false);
                                            // 실패 시 페이지에 머물러 재시도할 수 있게 한다
                                            if (!res?.success) {
                                                showError(res?.message || "취소 요청에 실패했습니다.", { duration: 3000, bottomPosition: 20 });
                                                return;
                                            }
                                            if (res.payment_cancel_warning) {
                                                // 예약 취소는 완료, PG 환불만 실패 — 사용자에게 알린다
                                                showError(res.payment_cancel_warning, { duration: 3000, bottomPosition: 20 });
                                            } else {
                                                showSuccess("예약이 취소되었습니다.", { duration: 3000, bottomPosition: 20 });
                                            }
                                            navigate(`/mybuccl`);
                                        });
                                    }}
                                >
                                    확인
                                </button>
                            </div>
                        </div>
                    </CommonModal>
                    <CommonModal
                        isOpen={showCancelWaitingModal}
                        onClose={() => setShowCancelWaitingModal(false)}
                    >
                        <div style={{ padding: "8px 0 4px" }}>
                            <p style={{ fontSize: "16px", fontWeight: "600", color: "#111", marginBottom: "24px", lineHeight: "1.5" }}>
                                예약 대기를 취소하시겠습니까?
                            </p>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    className="btn btn-gray200"
                                    style={{ flex: 1, height: "46px", borderRadius: "8px", fontSize: "15px" }}
                                    onClick={() => setShowCancelWaitingModal(false)}
                                >
                                    닫기
                                </button>
                                <button
                                    className="btn btn-purple500"
                                    style={{ flex: 1, height: "46px", borderRadius: "8px", fontSize: "15px" }}
                                    onClick={() => {
                                        cancelReservation(type, scheduleId).then((res) => {
                                            setShowCancelWaitingModal(false);
                                            // 실패 시 페이지에 머물러 재시도할 수 있게 한다
                                            if (!res?.success) {
                                                showError(res?.message || "취소 요청에 실패했습니다.", { duration: 3000, bottomPosition: 20 });
                                                return;
                                            }
                                            if (res.payment_cancel_warning) {
                                                // 예약 취소는 완료, PG 환불만 실패 — 사용자에게 알린다
                                                showError(res.payment_cancel_warning, { duration: 3000, bottomPosition: 20 });
                                            } else {
                                                showSuccess("예약 대기가 취소되었습니다.", { duration: 3000, bottomPosition: 20 });
                                            }
                                            navigate('/mybuccl');
                                        });
                                    }}
                                >
                                    확인
                                </button>
                            </div>
                        </div>
                    </CommonModal>
                </>
            )}
        </div>
    );
};

export default ClassReservationDetail;
